import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { doc, getDoc, getFirestore, serverTimestamp, setDoc } from 'firebase/firestore';
import { normalizeUnitGrid, type GridItem } from './unitLayout';

type Saved = { layout: GridItem[]; hidden: string[] };

/**
 * Per-user unit page grid, like the Today page: kept in the browser at once and in Firestore
 * `userPreferences/{uid}.unitGrid` so it follows the user. People who never arranged their own get
 * the shared default in `appDefaults/unitGrid`, which only the layout owner can write.
 */
export function useUnitLayout(uid: string | null | undefined) {
  const db = useMemo(() => getFirestore(), []);
  const localKey = `unit-page:grid:${uid || 'anon'}`;
  const readLocal = useCallback((): Saved | null => {
    try {
      const raw = window.localStorage.getItem(localKey);
      return raw ? normalizeUnitGrid(JSON.parse(raw)) : null;
    } catch {
      return null;
    }
  }, [localKey]);
  const [own, setOwn] = useState<Saved | null>(() => readLocal());
  const [orgDefault, setOrgDefault] = useState<Saved | null>(null);
  const saveTimer = useRef<number | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    getDoc(doc(db, 'appDefaults', 'unitGrid'))
      .then((snap) => {
        const d = snap.exists() ? normalizeUnitGrid(snap.data()) : null;
        if (!cancelled && d?.layout.length) setOrgDefault(d);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [db]);

  useEffect(() => {
    setOwn(readLocal());
    if (!uid) return;
    let cancelled = false;
    getDoc(doc(db, 'userPreferences', uid))
      .then((snap) => {
        if (cancelled || !snap.exists()) return;
        const remote = normalizeUnitGrid((snap.data() as any)?.unitGrid);
        setOwn(remote);
        try {
          if (remote) window.localStorage.setItem(localKey, JSON.stringify(remote));
          else window.localStorage.removeItem(localKey);
        } catch { /* ignore */ }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [db, localKey, readLocal, uid]);

  // null = follow the shared default again.
  const save = useCallback(
    (next: Saved | null) => {
      setOwn(next);
      try {
        if (next) window.localStorage.setItem(localKey, JSON.stringify(next));
        else window.localStorage.removeItem(localKey);
      } catch { /* ignore */ }
      if (!uid) return;
      window.clearTimeout(saveTimer.current);
      saveTimer.current = window.setTimeout(() => {
        setDoc(doc(db, 'userPreferences', uid), { unitGrid: next ? { ...next, updatedAt: serverTimestamp() } : null }, { merge: true }).catch((err) =>
          console.warn('Unit page: layout saved in this browser only', err?.code || err)
        );
      }, 700);
    },
    [db, localKey, uid]
  );
  useEffect(() => () => window.clearTimeout(saveTimer.current), []);

  const saveAsDefault = useCallback(
    async (next: Saved, email: string) => {
      await setDoc(doc(db, 'appDefaults', 'unitGrid'), { ...next, updatedBy: email, updatedAt: serverTimestamp() });
      setOrgDefault(next);
    },
    [db]
  );

  const active = own || orgDefault;
  return { saved: active ? active.layout : null, hidden: active ? active.hidden : [], isOwn: !!own, hasOrgDefault: !!orgDefault, save, saveAsDefault };
}
